import { CaptchaFormMessage } from '@eliadmin/core/src/components/providers/captcha-form-message';
import { useRecaptcha } from '@eliadmin/core/src/components/providers/captcha-provider';
import { FormErrorSummary } from './form-error-summary';
import { ROUTES } from '@eliadmin/core/src/constants/routes';
import {
  useFormValidationMessages,
  type FormValidationMessages,
} from '@eliadmin/core/src/hooks/use-form-validation-messages';
import { z } from 'zod';
import { zodResolver } from '@hookform/resolvers/zod';
import { useForm } from 'react-hook-form';
import { useTranslations } from '@eliadmin/core/src/hooks/use-translations';
import { useLocaleStore } from '@eliadmin/core/src/stores/locale-store';
import { Field } from './field';
import { Form } from '@eliadmin/core/src/components/ui/form';
import { useQuery } from '@tanstack/react-query';
import { CacheKey } from '@eliadmin/core/src/api/constants';
import { fetchCountries } from '@eliadmin/core/src/api/general/general-api';
import Link from '@eliadmin/core/src/components/link';
import { Button } from '@eliadmin/core/src/components/ui/button';
import { registerCompany } from '@eliadmin/core/src/api/jobportal/jobporta-auth-api';
import { useRouter } from 'next/navigation';
import { useToast } from '@eliadmin/core/src/components/ui/use-toast';
import { setAuthToken } from '@eliadmin/core/src/server/api-client';
import { initCustomerStore } from '@eliadmin/core/src/stores/customer-store';
import { ValidationError } from '@eliadmin/core/src/api/errors';
import { fetchJobCategories } from '@eliadmin/core/src/api/jobportal/categories-api';
import { useRef, useState } from 'react';
import { Spinner } from '@eliadmin/core/src/components/ui/spinner';

import { trackUserActions } from '../analytics/analytics-helper';

export const registerCompanySchema = (VM: FormValidationMessages) =>
  z
    .object({
      name: z.string().min(1, VM.CANT_BE_EMPTY_VALIDATION_MESSAGE()), // Nazov firmy
      www: z.string(), // Company website
      ico: z.string().min(8, VM.MIN_LENGTH_VALIDATION_MESSAGE(8)), // ICO
      dic: z.string().min(10, VM.MIN_LENGTH_VALIDATION_MESSAGE(10)),
      ic_dph: z.string().optional(), // IC DPH
      ic_dph_hidden: z.boolean().optional(), // Show IC DPH
      address: z.string().min(1, VM.CANT_BE_EMPTY_VALIDATION_MESSAGE()), // ulica firmy
      street_number: z.string().min(1, VM.CANT_BE_EMPTY_VALIDATION_MESSAGE()), // orientacne cislo
      postcode: z.string().min(1, VM.CANT_BE_EMPTY_VALIDATION_MESSAGE()), // Postcode
      city: z.string().min(1, VM.CANT_BE_EMPTY_VALIDATION_MESSAGE()), // Mesto
      country: z.object({ id: z.string().min(1, VM.CANT_BE_EMPTY_VALIDATION_MESSAGE()) }), // Select field, country id
      phone: z.string().min(5, VM.CANT_BE_EMPTY_VALIDATION_MESSAGE()), // Phone number
      email: z
        .string()
        .min(1, VM.CANT_BE_EMPTY_VALIDATION_MESSAGE())
        .email(VM.EMAIL_INVALID_VALIDATION_MESSAGE()),
      contact_name: z.string().min(1, VM.CANT_BE_EMPTY_VALIDATION_MESSAGE()), // Contact person name
      title_before: z.string().optional(), // Contact person title before
      title_after: z.string().optional(), // Contact person title after
      contact_surname: z.string().min(1, VM.CANT_BE_EMPTY_VALIDATION_MESSAGE()), // Contact person surname
      description: z.string().optional(), // Description
      categories: z.array(z.any()), // Categories
      register_email: z.string().email(VM.EMAIL_INVALID_VALIDATION_MESSAGE()), // Register email
      password: z.string().min(8, VM.MIN_LENGTH_VALIDATION_MESSAGE(8)), // Password
      password_confirmation: z.string().min(8, VM.MIN_LENGTH_VALIDATION_MESSAGE(8)), // Password confirmation
      newsletter: z.boolean().optional(), // Newsletter checkbox
      newsletter_return_url: z.string().optional(), // Newsletter return url
      gdpr: z.boolean().refine(val => val === true, { message: VM.GDPR_VALIDATION_MESSAGE() }), // GDPR checkbox
      'g-recaptcha-response': z.string().default(''),
    })
    .refine(data => data.password === data.password_confirmation, {
      message: VM.PASSWORD_CONFIRMATION_DONT_MATCH_VALIDATION_MESSAGE(),
      path: ['password_confirmation'],
    });

type RegisterCompanySchema = ReturnType<typeof registerCompanySchema>;
type RegisterFormCompanyInputValues = z.input<RegisterCompanySchema>;
export type RegisterFormCompanyValues = z.output<RegisterCompanySchema>;

export const RegisterFormCompany = () => {
  const { t } = useTranslations();
  const locale = useLocaleStore(s => s.currentLocale);
  const { formValidationMessages } = useFormValidationMessages(t);
  const [isLoading, setIsLoading] = useState(false);
  const submitInFlight = useRef(false);
  const { executeRecaptcha } = useRecaptcha();

  const { data: countries } = useQuery({
    queryKey: [CacheKey.COUNTRIES, locale],
    queryFn: () => fetchCountries({ locale }),
    refetchOnWindowFocus: false,
  });

  const { data: categories } = useQuery({
    queryKey: [CacheKey.JOBPORTAL_CATEGORIES],
    queryFn: () =>
      fetchJobCategories({ locale, query: { results: 150 } }).then(res =>
        res.data.map(o => {
          return { label: o.name, value: o.id };
        }),
      ),
  });

  const form = useForm<RegisterFormCompanyInputValues, any, RegisterFormCompanyValues>({
    resolver: zodResolver(registerCompanySchema(formValidationMessages)),
    defaultValues: {
      name: '',
      address: '',
      street_number: '',
      postcode: '',
      city: '',
      country: { id: '' },
      phone: '',
      email: '',
      ico: '',
      dic: '',
      ic_dph_hidden: false,
      ic_dph: '',
      www: '',
      contact_name: '',
      contact_surname: '',
      register_email: '',
      password: '',
      password_confirmation: '',
      'g-recaptcha-response': '',
      newsletter: false,
      categories: [],
      newsletter_return_url: `${process.env.NEXT_PUBLIC_APP_URL}${ROUTES.NEWSLETTER_CONFIRM()}`,
      gdpr: false,
    },
  });

  const { toast } = useToast();
  const router = useRouter();

  const handleSubmit = form.handleSubmit(async data => {
    if (!data) return;
    if (submitInFlight.current) return;
    submitInFlight.current = true;
    setIsLoading(true);
    try {
      const captchaRes = executeRecaptcha ? await executeRecaptcha('register') : '';

      data.categories = data.categories?.map(category => ({ id: parseInt(category.value) })) || [];

      const resp = await registerCompany({
        ...data,
        country: data.country.id,
        'g-recaptcha-response': captchaRes || '',
        locale,
      });
      await setAuthToken({ authToken: resp.data.token });
      initCustomerStore({
        authToken: resp.data.token,
        customer: resp.data.customer,
      });
      trackUserActions.registration({ method: 'form', user_type: 'company', email: data.email });
      toast({
        title: t('text654'),
        variant: 'success',
      });
      router.replace(ROUTES.PROFILE());
    } catch (e) {
      // handle 422
      if (e instanceof ValidationError) {
        for (const [field, errors] of Object.entries(e.fields)) {
          form.setError(field as any, { type: 'manual', message: errors.join('\n') });
        }
        return;
      }

      toast({
        title: t('text653'),
        description: (e as any).message,
        variant: 'destructive',
      });
    } finally {
      submitInFlight.current = false;
      setIsLoading(false);
    }
  });

  return (
    <Form {...form}>
      <CaptchaFormMessage errors={form.formState.errors} />
      <form onSubmit={handleSubmit} className="flex flex-col gap-4 md:gap-6">
        <FormErrorSummary errors={form.formState.errors} />
        <div className="flex w-full flex-col gap-4 rounded-lg bg-white p-4 md:gap-[28px] md:p-8">
          <h2 className="text-2xl font-bold md:text-3xl">{t('text1331')}</h2>
          <Field
            control={form.control}
            name="name"
            label={t('text1432')}
            required
            placeholder={t('text1432') + '...'}
          />
          <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
            <Field
              control={form.control}
              name="ico"
              label={t('text42')}
              required
              placeholder={t('text42') + '...'}
            />
            <Field
              control={form.control}
              name="dic"
              label={t('text43')}
              required
              placeholder={t('text43') + '...'}
            />
            <Field
              control={form.control}
              name="ic_dph"
              label={t('text44')}
              placeholder={t('text44') + '...'}
              isDisabled={form.watch('ic_dph_hidden')}
            />
            <div className="flex h-min items-center md:h-20 md:pt-4">
              <Field
                control={form.control}
                name="ic_dph_hidden"
                type="checkbox"
                label={t('text1876')}
              />
            </div>
          </div>
          <h3 className="text-lg font-bold md:text-2xl">{t('text1723')}</h3>
          <div className="grid grid-cols-1 gap-2 gap-y-4 md:grid-cols-2 md:gap-4 md:gap-y-6">
            <Field
              control={form.control}
              name="address"
              label={t('text38')}
              required
              placeholder={t('text38') + '...'}
            />
            <Field
              control={form.control}
              name="street_number"
              label={t('text485')}
              required
              placeholder={t('text485') + '...'}
            />
            <Field
              control={form.control}
              name="city"
              label={t('text39')}
              required
              placeholder={t('text39') + '...'}
            />
            <Field
              control={form.control}
              name="postcode"
              label={t('text40')}
              required
              placeholder={t('text40') + '...'}
            />
          </div>
          <Field
            control={form.control}
            name="country.id"
            type="select"
            required
            options={countries?.data.map(c => ({ value: c.id, label: c.name })) || []}
            label={t('text41')}
          />
          <Field
            control={form.control}
            name="www"
            label={t('text1725')}
            placeholder={'https://...'}
          />
        </div>
        <div className="flex w-full flex-col gap-4 rounded-lg bg-white p-4 md:gap-[28px] md:p-8">
          <h2 className="text-2xl font-bold md:text-3xl">{t('text1724')}</h2>
          <div className="grid grid-cols-1 gap-2 gap-y-4 md:grid-cols-2 md:gap-4 md:gap-y-6">
            <Field
              control={form.control}
              name="title_before"
              label={t('text1726')}
              placeholder={t('text1726') + '...'}
            />
            <Field
              control={form.control}
              name="title_after"
              label={t('text1755')}
              placeholder={t('text1755') + '...'}
            />
          </div>
          <Field
            control={form.control}
            name="contact_name"
            label={t('text33')}
            required
            placeholder={t('text33') + '...'}
          />
          <Field
            control={form.control}
            name="contact_surname"
            label={t('text34')}
            required
            placeholder={t('text34') + '...'}
          />
          <div className="grid grid-cols-1 gap-2 gap-y-4 md:grid-cols-2 md:gap-4 md:gap-y-6">
            <Field
              control={form.control}
              name="phone"
              label={t('text36')}
              type="tel"
              required
              placeholder={t('text36') + '...'}
            />
            <Field
              control={form.control}
              name="email"
              label={t('text35')}
              required
              placeholder={t('text35') + '...'}
            />
          </div>
        </div>
        <div className="flex w-full flex-col gap-4 rounded-lg bg-white p-4 md:gap-[28px] md:p-8">
          <h2 className="text-2xl font-bold md:text-3xl">{t('text1463')}</h2>
          <Field
            control={form.control}
            name="description"
            type="textarea"
            label={t('text1727')}
            placeholder={t('text1727') + '...'}
          />
          {/* <Field
            control={form.control}
            name="categories"
            type="multiselect"
            label={t('text1496')}
            required
            placeholder={t('text1728')}
            options={categories || []}
          /> */}
        </div>
        <div className="flex w-full flex-col gap-4 rounded-lg bg-white p-4 md:gap-[28px] md:p-8">
          <h2 className="text-2xl font-bold md:text-3xl">{t('text385')}</h2>
          <Field
            control={form.control}
            name="register_email"
            label={t('text35')}
            placeholder={t('text35') + '...'}
          />
          <div className="grid grid-cols-1 gap-2 gap-y-4 md:grid-cols-2 md:gap-4 md:gap-y-6">
            <Field
              control={form.control}
              name="password"
              type="password"
              label={t('text191')}
              required
              placeholder={t('text191') + '...'}
            />
            <Field
              control={form.control}
              name="password_confirmation"
              type="password"
              label={t('text1108')}
              required
              placeholder={t('text1108') + '...'}
            />
          </div>
        </div>
        <div className="flex w-full flex-col gap-4 rounded-lg bg-white p-4 md:gap-[28px] md:p-8">
          <Field
            control={form.control}
            name="newsletter"
            type="checkbox"
            label={t(
              'Súhlasím s odberom mesačného newslettera o trhu práce a emailov s marketingovými informáciami na kontaktnú adresu uvedenú v registračnom formulári.',
            )}
          />
          <Field
            control={form.control}
            name="gdpr"
            type="checkbox"
            label={
              <div>
                {t('text613')}{' '}
                <Link href={ROUTES.GDPR()} target="_blank" className="text-primary">
                  {t('text614')}
                </Link>{' '}
                {t('a pravidlami používania stránky')}
              </div>
            }
          />
        </div>
        <div className="flex w-full justify-center">
          <Button className="w-full md:w-auto" disabled={isLoading}>
            {isLoading ? <Spinner /> : t('text192')}
          </Button>
        </div>
      </form>
    </Form>
  );
};
